import type { Entry } from "contentful";
import type { ReactElement } from "react";
import CTAButton from "@/components/custom/CtaButton/CTAButton";
import { resolveCtaHref } from "@/components/custom/CtaButton/ctaHref";
import { EmailForm } from "@/components/custom/EmailForm/EmailForm";
import { Prose } from "@/components/custom/Prose";
import { SingleImage } from "@/components/custom/SingleImage";
import { getEntryOfContentType } from "@/lib/contentful/guards";
import type {
  TypeButton,
  TypeButtonSkeleton,
  TypeEmailFormSkeleton,
  TypeExternalButton,
  TypeExternalButtonSkeleton,
  TypeImageSkeleton,
  TypeProseSkeleton,
} from "@/types/contentful/generated";
import { ContentfulHelper } from "@/utils/ContentfulHelper";

type EmailFormEntry = Entry<TypeEmailFormSkeleton, undefined, string>;
type ProseEntry = Entry<TypeProseSkeleton, undefined, string>;
type ImageEntry = Entry<TypeImageSkeleton, undefined, string>;

export type ContentBlock = Entry<
  TypeEmailFormSkeleton | TypeButtonSkeleton | TypeExternalButtonSkeleton | TypeImageSkeleton | TypeProseSkeleton,
  undefined,
  string
>;

interface Props {
  block: ContentBlock;
  onCtaClick?: () => void;
  popupId?: string;
  shownAt?: number;
}

/**
 * The sizes attribute for the SingleImage component within EmailPromoModalClient.
 * This ensures that the image is appropriately sized for the modal dialog rather than using the default sizes.
 */
export const PROMO_IMAGE_SIZES = "(min-width: 452px) 372px, calc(100vw - 5rem)";

function determineBlockToRender(block: ContentBlock, handlers: Omit<Props, "block"> = {}): ReactElement | null {
  const { onCtaClick, popupId, shownAt } = handlers;

  const emailForm = getEntryOfContentType<EmailFormEntry>(block, "emailForm");
  if (emailForm) {
    return <EmailForm {...emailForm.fields} errorVariant="badge" popupId={popupId} shownAt={shownAt} />;
  }

  const prose = getEntryOfContentType<ProseEntry>(block, "prose");
  if (prose) {
    return <Prose content={prose.fields.content} />;
  }

  const imageEntry = getEntryOfContentType<ImageEntry>(block, "image");
  if (imageEntry) {
    const image = ContentfulHelper.parseContentfulContentImage(imageEntry.fields.media);
    // An unpublished asset resolves as an entry but has nothing to show.
    if (!image) return null;

    return <SingleImage image={image} entryId={imageEntry.sys.id} sizes={PROMO_IMAGE_SIZES} />;
  }

  const button =
    getEntryOfContentType<TypeButton<undefined, string>>(block, "button") ??
    getEntryOfContentType<TypeExternalButton<undefined, string>>(block, "externalButton");

  /**
   *CTAButton renders nothing without a destination (unpublished page, no slug, blank URL):
   * so the block can't be counted either.
   * Baseline-only: when these CTAs become personalized, a variant with a destination
   * behind a destination-less baseline will be dropped here.
   */
  if (button) {
    if (!resolveCtaHref(button)) return null;
    return <CTAButton entry={button} className="self-center" onCtaClick={onCtaClick} popupId={popupId} />;
  }

  return null;
}

/**
 * Whether this block would render anything. The server counts blocks before React renders
 * them, so without this an unpublished image asset — or a content type the dispatch doesn't
 * handle — opens a promo dialog with nothing in it.
 */
export function isRenderableBlock(block: ContentBlock): boolean {
  return determineBlockToRender(block) !== null;
}

export function EmailPromoModalBlock({ block, ...handlers }: Props) {
  return determineBlockToRender(block, handlers);
}
